/**
 * NodeCard - Collapsible chain-of-thought card for each processing stage.
 * Supports pending/active/done states to show plan progress.
 */
import { useState, useEffect } from 'react';
import { BrandSpinner } from './BrandSpinner';

function Rationale({ children }) {
  if (!children) return null;
  return (
    <p className="text-[12px] text-gray-500 leading-relaxed mb-1.5 pb-1.5 border-b border-gray-100">
      {children}
    </p>
  );
}

function ElapsedTime({ timestamp, frozen }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!timestamp) return;
    setElapsed(Math.round((Date.now() - timestamp) / 1000));
    if (frozen) return;
    const interval = setInterval(() => {
      setElapsed(Math.round((Date.now() - timestamp) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [timestamp, frozen]);

  if (!timestamp) return null;
  return <span className="text-[11px] text-gray-400 tabular-nums ml-auto">{elapsed}s</span>;
}

function QueryPills({ queries }) {
  if (!queries || queries.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {queries.map((q, i) => (
        <span key={i} className="bg-white border border-gray-200 text-gray-600 text-[11px] px-2 py-0.5 rounded-full">
          {q}
        </span>
      ))}
    </div>
  );
}

function ConfidenceBadge({ confidence }) {
  if (!confidence) return null;
  const colors = {
    high: 'bg-emerald-100 text-emerald-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-red-100 text-red-700',
  };
  const colorClass = colors[confidence.toLowerCase()] || colors.medium;
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${colorClass}`}>
      {confidence}
    </span>
  );
}

function RouterContent({ data }) {
  const queryCount = data.sub_queries?.length || 0;
  const rationale = data.rationale ||
    (queryCount > 0
      ? `Decomposed into ${queryCount} search quer${queryCount === 1 ? 'y' : 'ies'} to cover different aspects of this question.`
      : null);

  return (
    <div className="space-y-1.5">
      <Rationale>{rationale}</Rationale>
      {data.query_type && (
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-200/80 text-gray-500 font-medium">
          {data.query_type.replace(/_/g, ' ')}
        </span>
      )}
      <QueryPills queries={data.sub_queries} />
    </div>
  );
}

function ResearchContent({ data }) {
  const perQuery = data.per_query || [];
  const hasMerges = (data.cross_query_merges || 0) > 0;
  const totalKept = perQuery.reduce((sum, pq) => sum + (pq.kept || 0), 0);
  const totalRetrieved = perQuery.reduce((sum, pq) => sum + (pq.retrieved || 0), 0);

  const rationale = perQuery.length > 0
    ? `Searched ${perQuery.length} quer${perQuery.length === 1 ? 'y' : 'ies'} against the knowledge base. Retrieved ${totalRetrieved} chunks, graded for relevance, and kept ${totalKept}. ${hasMerges ? `Merged ${data.cross_query_merges} overlapping chunks across queries. ` : ''}${data.total_sources || 0} unique sources remain for analysis.`
    : null;

  return (
    <div className="space-y-2">
      <Rationale>{rationale}</Rationale>
      {/* Per-sub-query breakdown */}
      {perQuery.length > 0 ? (
        <div className="space-y-1.5">
          {perQuery.map((pq, i) => (
            <div key={i} className="flex items-start gap-2 py-1 px-2 rounded-md bg-white/60 border border-gray-100">
              <span className="text-[11px] text-gray-500 mt-0.5 shrink-0">{i + 1}.</span>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-gray-600 leading-snug truncate">{pq.query}</p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                    {pq.retrieved} retrieved
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${pq.kept > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                    {pq.kept} kept
                  </span>
                  {pq.expanded > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-500">
                      {pq.expanded} expanded
                    </span>
                  )}
                  {pq.consolidated > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-500">
                      {pq.consolidated} merged
                    </span>
                  )}
                  {pq.error && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-500">error</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <QueryPills queries={data.sub_queries} />
      )}

      {/* Summary badges */}
      <div className="flex flex-wrap gap-1.5">
        {data.total_sources > 0 && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">
            {data.total_sources} final sources
          </span>
        )}
        {hasMerges && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 font-medium">
            {data.cross_query_merges} cross-query merges
          </span>
        )}
        {data.total_sources_before_dedup > 0 && data.total_sources_before_dedup !== data.total_sources && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
            {data.total_sources_before_dedup} before dedup
          </span>
        )}
      </div>
    </div>
  );
}

function EvaluationContent({ data }) {
  const followUps = data.follow_up_documents || [];
  const gapCount = data.gaps?.length || 0;
  const strategy = data.recovery_strategy || 'none';
  const confidence = (data.confidence || '').toLowerCase();

  let rationale;
  if (strategy === 'none' || !strategy) {
    rationale = confidence === 'high'
      ? 'Research results are sufficient. Proceeding to synthesise the response.'
      : `Confidence is ${confidence}, but results are adequate. Proceeding to synthesise the response.`;
  } else {
    const actionParts = [];
    if (strategy === 'reformulate' || strategy === 'both') actionParts.push('reformulating search queries');
    if (strategy === 'follow_citations' || strategy === 'both') actionParts.push(`chasing ${followUps.length} referenced document${followUps.length !== 1 ? 's' : ''}`);
    rationale = `Confidence is ${confidence}${gapCount > 0 ? ` with ${gapCount} gap${gapCount !== 1 ? 's' : ''} identified` : ''}. Refining the search by ${actionParts.join(' and ')}.`;
  }

  return (
    <div className="space-y-2">
      <Rationale>{rationale}</Rationale>
      <div className="flex items-center gap-2 flex-wrap">
        <ConfidenceBadge confidence={data.confidence} />
        {data.recovery_strategy && data.recovery_strategy !== 'none' && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-200/80 text-gray-500 font-medium">
            {data.recovery_strategy.replace(/_/g, ' ')}
          </span>
        )}
        {data.retry_count > 0 && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 font-medium">
            Retry {data.retry_count}
          </span>
        )}
      </div>
      {data.gaps && data.gaps.length > 0 && (
        <div>
          <p className="text-[11px] text-gray-400 font-medium mb-0.5">Gaps identified:</p>
          <ul className="space-y-0.5">
            {data.gaps.map((gap, i) => (
              <li key={i} className="text-[12px] text-gray-500 flex items-start gap-1.5">
                <span className="text-gray-300 mt-0.5">•</span>
                <span>{gap}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {followUps.length > 0 && (
        <div>
          <p className="text-[11px] text-gray-400 font-medium mb-0.5">References to chase:</p>
          <ul className="space-y-0.5">
            {followUps.map((doc, i) => (
              <li key={i} className="text-[12px] text-gray-500 flex items-start gap-1.5">
                <span className="text-blue-400 mt-0.5">→</span>
                <span>
                  <span className="font-medium text-gray-600">{doc.raw_reference}</span>
                  {doc.reason && <span className="text-gray-400"> — {doc.reason}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ReformulationContent({ data }) {
  const queryCount = data.new_queries?.length || 0;
  const rationale = data.guidance
    ? `${data.guidance}${queryCount > 0 ? ` Generated ${queryCount} new search quer${queryCount === 1 ? 'y' : 'ies'}.` : ''}`
    : queryCount > 0
      ? `Generated ${queryCount} refined search quer${queryCount === 1 ? 'y' : 'ies'} to address the identified gaps.`
      : null;

  return (
    <div className="space-y-1.5">
      <Rationale>{rationale}</Rationale>
      <QueryPills queries={data.new_queries} />
      {data.retry_count > 0 && (
        <span className="inline-block text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 font-medium mt-1">
          Retry {data.retry_count}
        </span>
      )}
    </div>
  );
}

function ConsolidateContent() {
  return (
    <Rationale>
      Combining research findings from all sources into a coherent response with citations.
    </Rationale>
  );
}

const contentRenderers = {
  router: RouterContent,
  research: ResearchContent,
  semantic_evaluate: EvaluationContent,
  reformulate_plan: ReformulationContent,
  consolidate: ConsolidateContent,
};

export function NodeCard({ node, label, status, timestamp, data }) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Auto-expand when active or just completed
  useEffect(() => {
    if (status === 'active') setIsExpanded(true);
  }, [status]);

  // Auto-collapse done cards after 1.5s
  useEffect(() => {
    if (status === 'done') {
      setIsExpanded(true); // briefly show content
      const timer = setTimeout(() => setIsExpanded(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const ContentRenderer = contentRenderers[node];
  const isDone = status === 'done';
  const isPending = status === 'pending';
  const isActive = status === 'active';

  return (
    <div className={`mb-1.5 rounded-lg border overflow-hidden transition-all duration-300 ${
      isPending ? 'border-gray-100 bg-gray-50/40' : 'border-gray-200/80 bg-gray-50/60'
    }`}>
      <button
        onClick={() => !isPending && setIsExpanded(!isExpanded)}
        className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-left transition-colors ${
          isPending ? 'cursor-default' : 'hover:bg-gray-100/60'
        }`}
      >
        {/* Chevron - hidden for pending */}
        {!isPending ? (
          <svg
            className={`w-3 h-3 text-gray-400 transition-transform duration-200 shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
            viewBox="0 0 8 12" fill="none"
          >
            <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <div className="w-3 h-3 shrink-0" />
        )}

        {/* Status icon */}
        {isDone ? (
          <svg className="w-3.5 h-3.5 text-emerald-500 shrink-0" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1" />
            <path d="M5 8.5L7 10.5L11 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : isActive ? (
          <div className="w-3.5 h-3.5 shrink-0 relative flex items-center justify-center">
            <BrandSpinner size="sm" alt="" />
          </div>
        ) : (
          <svg className="w-3.5 h-3.5 text-gray-300 shrink-0" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        )}

        {/* Label */}
        <span className={`text-[12px] font-medium transition-colors duration-300 ${
          isPending ? 'text-gray-400' : isActive ? 'text-gray-700' : 'text-gray-600'
        }`}>{label}</span>

        {!isPending && <ElapsedTime timestamp={timestamp} frozen={isDone} />}
      </button>

      {/* Expandable body - only for active/done with content */}
      {!isPending && ContentRenderer && data && (
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="px-3.5 pb-2.5 pt-0.5 border-t border-gray-200/60">
            <ContentRenderer data={data} />
          </div>
        </div>
      )}
    </div>
  );
}
